import { ROUTE_METERS_CHANGED_RATIO } from "./constants";
import { collectGreedyMatches } from "./collectGreedyMatches";
import { euclideanDistance, extentsCenter, groupByPage } from "./matching";
import type { ScaledRoute } from "../../../utils/types";
import type { DiffSide } from "../computeQuantityDiff";
import type {
  MatchCandidate,
  MatchRoutes,
  VisualDiffPoint,
  VisualDiffRoute,
  VisualDiffStatus,
} from "./types";

const toVisualDiffRoute = (
  route: ScaledRoute,
  side: DiffSide,
  status: VisualDiffStatus,
  typeKey: string,
): VisualDiffRoute => ({
  route: { ...route, id: `${side}:${route.id}` },
  side,
  status,
  typeKey,
});

const sortPointsByCoordinates = (
  points: VisualDiffPoint[],
): VisualDiffPoint[] =>
  [...points].sort((firstPoint, secondPoint) =>
    firstPoint.x !== secondPoint.x
      ? firstPoint.x - secondPoint.x
      : firstPoint.y - secondPoint.y,
  );

const buildEndpointSignature = (route: ScaledRoute): VisualDiffPoint[] => {
  const nodes = route.nodes ?? [];
  const edges = route.edges ?? [];
  if (nodes.length === 0) return [extentsCenter(route)];
  if (nodes.length < 2 || edges.length === 0) {
    return sortPointsByCoordinates(
      nodes.map((node) => ({ x: node.x, y: node.y })),
    );
  }
  const degreeByNodeId = new Map<number, number>();
  for (const edge of edges) {
    degreeByNodeId.set(edge.from, (degreeByNodeId.get(edge.from) ?? 0) + 1);
    degreeByNodeId.set(edge.to, (degreeByNodeId.get(edge.to) ?? 0) + 1);
  }
  const endpointNodes = nodes.filter(
    (node) => (degreeByNodeId.get(node.id) ?? 0) <= 1,
  );
  // A pure cycle has no degree-1 nodes; its bounding-box center still localizes it.
  if (endpointNodes.length === 0) return [extentsCenter(route)];
  return sortPointsByCoordinates(
    endpointNodes.map((node) => ({ x: node.x, y: node.y })),
  );
};

// Pairs every point of the smaller signature to its nearest unused counterpart;
// the score is the worst paired distance, so ALL shared endpoints must align.
const scoreEndpointAlignment = (
  firstSignature: VisualDiffPoint[],
  secondSignature: VisualDiffPoint[],
): number => {
  const [smallerSignature, largerSignature] =
    firstSignature.length <= secondSignature.length
      ? [firstSignature, secondSignature]
      : [secondSignature, firstSignature];
  const usedLargerIndices = new Set<number>();
  let worstDistance = 0;
  for (const point of smallerSignature) {
    let bestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < largerSignature.length; index += 1) {
      if (usedLargerIndices.has(index)) continue;
      const distance = euclideanDistance(point, largerSignature[index]);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    }
    if (bestIndex === -1) return Number.POSITIVE_INFINITY;
    usedLargerIndices.add(bestIndex);
    worstDistance = Math.max(worstDistance, bestDistance);
  }
  return worstDistance;
};

const areMetersClose = (beforeMeters: number, afterMeters: number): boolean => {
  const largest = Math.max(beforeMeters, afterMeters);
  if (largest === 0) return true;
  return (
    Math.abs(afterMeters - beforeMeters) <= ROUTE_METERS_CHANGED_RATIO * largest
  );
};

export const matchRoutes: MatchRoutes = ({
  beforeRoutes,
  afterRoutes,
  tolerancePdfPoints,
  typeKey,
}) => {
  const results: VisualDiffRoute[] = [];
  const beforeByPage = groupByPage(beforeRoutes, (route) => route.page ?? 0);
  const afterByPage = groupByPage(afterRoutes, (route) => route.page ?? 0);
  const pageIndices = new Set([...beforeByPage.keys(), ...afterByPage.keys()]);

  for (const pageIndex of pageIndices) {
    const beforeBucket = beforeByPage.get(pageIndex) ?? [];
    const afterBucket = afterByPage.get(pageIndex) ?? [];
    const beforeSignatures = beforeBucket.map(buildEndpointSignature);
    const afterSignatures = afterBucket.map(buildEndpointSignature);

    // NOTE: quadratic per type (~100ms at 500 routes) — past ~300, index after-routes by endpoint in a cell grid like matchMarkers.
    const candidates: MatchCandidate[] = [];
    for (
      let beforeIndex = 0;
      beforeIndex < beforeBucket.length;
      beforeIndex += 1
    ) {
      for (
        let afterIndex = 0;
        afterIndex < afterBucket.length;
        afterIndex += 1
      ) {
        const score = scoreEndpointAlignment(
          beforeSignatures[beforeIndex],
          afterSignatures[afterIndex],
        );
        if (score > tolerancePdfPoints) continue;
        candidates.push({
          beforeIndex,
          afterIndex,
          distance: score,
          beforeId: beforeBucket[beforeIndex].id,
          afterId: afterBucket[afterIndex].id,
        });
      }
    }

    const { pairs, matchedBeforeIndices, matchedAfterIndices } =
      collectGreedyMatches(candidates);
    for (const pair of pairs) {
      const beforeRoute = beforeBucket[pair.beforeIndex];
      const afterRoute = afterBucket[pair.afterIndex];
      const status: VisualDiffStatus = areMetersClose(
        beforeRoute.meters,
        afterRoute.meters,
      )
        ? "unchanged"
        : "changed";
      results.push(toVisualDiffRoute(afterRoute, "after", status, typeKey));
    }
    afterBucket.forEach((route, index) => {
      if (matchedAfterIndices.has(index)) return;
      results.push(toVisualDiffRoute(route, "after", "added", typeKey));
    });
    beforeBucket.forEach((route, index) => {
      if (matchedBeforeIndices.has(index)) return;
      results.push(toVisualDiffRoute(route, "before", "removed", typeKey));
    });
  }

  return results;
};
